import {Module, PubSub, Script, Tracking2, Viewport} from '@mamdev/cad.static-next-lib';
import UrlPath from '../../ts/services/url-path';
import {
  type VideoInfo,
  type videoEventType,
  type EventData,
  type AdInfo,
  type AdFormat
} from '@mamdev/cad.static-next-lib';
import {
  type AdErrorEvent,
  type AdEvent,
  type AdLoadedXMLEvent,
  type BeforePlayEvent,
  type FullscreenEvent,
  type GlobalPlayerConfig,
  type JWPlayer,
  type PlaybackEvent,
  type PlayerConfig,
  type PlayerErrorEvent,
  type PlayerEvent,
  type TrackingClickData,
  type TrackingData
} from '../../ts/services/video-player.types';
import {isFullscreenAPIAvailableForElements} from './utils';
import {autoRedirectStorageKey, type Revolver, type RevolverTrigger} from './revolver';
import {log} from '../../ts/utils/decorators';

// Type mapping for lowercase ad types the AdInfo object uses to avoid runtime checks.
const adTypeMap: Record<'bumperad' | 'preRoll' | 'postRoll', AdFormat> = {
  bumperad: 'bumperad',
  preRoll: 'preroll',
  postRoll: 'postroll'
};

export interface AdData {
  adType: 'preroll' | 'postroll' | 'bumperad';
  adUrl: string;
}

export default class Newvideo extends Module {
  licenceKey = 'DgUPa14OkdIJ6dyjHjl4Cy70U8CCDKjLBmSdFTMmumkmLIl2';
  placeHolderImage: HTMLElement | null;
  playerId: string | undefined;
  player: JWPlayer;
  eventPhase: 'ready' | 'running' | 'paused' | 'finished' = 'ready';
  fullScreen = false;
  floating = false;
  expanded = false;
  specificPlayerCfg?: PlayerConfig;
  brainVidUrl?: string;
  videoduration: number;
  autoplay: boolean;
  autoplayMuted: boolean;
  muted = false;
  mutedProgrammatically = false; // Used in the onMute callback to distinguish between user action or not.
  adsPlayedByType = {preRoll: 0, postRoll: 0, bumperad: 0};
  useHomad = false;
  homadRequested = false;
  playerIsReady = false;
  vastUrls = {preroll: '', postroll: '', bumperad: ''};
  bumperAdsActivated = false;
  postRollsActivated = true;
  usePrebid = false;
  prebidTimeout = {conf: 0, triggered: false};
  adDuration = 0;
  creativeid: string | null;
  playerAlreadySetUp = false;
  playbackStarted = false;
  playerCurrentTime = 0;
  watched0 = false;
  watched25 = false;
  watched50 = false;
  watched75 = false;
  layoutClass = 'b';
  private revolver: Revolver; // Instance of Revolver class
  private revolverRoot: HTMLElement | null;
  private resumePlaybackAfterClosingRevolver = false;
  private pageScrollTop = 0;
  private moreVideosToastShown = false;
  private pageLoadedAsAResultOfRevolverAutoRedirect = false;

  // Keep track of which ad type was played last to pass this info to the tracking, since we can't get it from the JWPlayer event.
  private lastAdPlayedFormat: AdFormat;

  componentPath: string;
  contentType: 'video' | 'video.external-video' | 'video.vertical-video';
  articleId: string;
  videoPosition: string;
  videoProvider: string;
  currentCoremId: string;
  globalPlayerConfig: GlobalPlayerConfig;
  curBrand: 'webde' | 'gmx' | 'eue';
  region: string;
  customFullscreenPlaceholder: HTMLElement | undefined;

  /**
   * Constructor from DOM
   */
  static attachTo(root: HTMLElement): Newvideo {
    return new Newvideo(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'newvideo';
    this.placeHolderImage = this.root_.querySelector('[data-mod-name="native-image"]');
    this.playerId = this.root_.dataset.playerid;
    this.specificPlayerCfg = this.playerId ? window?.ui?.videoPlayers[this.playerId] as PlayerConfig : undefined;
    this.componentPath = this.specificPlayerCfg?.videoPlayerTracking2Model?.componentPath ?? '';
    this.contentType = this.specificPlayerCfg?.videoPlayerTracking2Model?.contentType ?? 'video';
    this.articleId = this.specificPlayerCfg?.videoPlayerTracking2Model?.articleId ?? '';
    this.videoPosition = this.specificPlayerCfg?.videoposition ?? '';
    this.videoProvider = this.specificPlayerCfg?.videoPlayerTracking2Model?.contentProvider ?? '';
    this.videoduration = this.specificPlayerCfg?.duration ?? 0;
    this.currentCoremId = window.ui.pageinfo.coremid.toString() ?? '';
    this.curBrand = window.ui.pageinfo.brand;
    this.region = window.ui.pageinfo.region ?? '';
  }

  /**
   * Starts the module
   */
  start() {
    try {
      this.log('start');

      // collecting playerIds
      window.videoplayer = window.videoplayer || [];

      // prebid toggler
      const dataPrebid = this.root_.dataset.prebid;
      this.usePrebid = (dataPrebid ? dataPrebid === 'true' : false);
      this.log('use prebid: ' + this.usePrebid);

      this.useHomad = this.useHomad || (!!this.specificPlayerCfg && this.specificPlayerCfg.homad === true);
      this.log('use homad: ' + this.useHomad);

      this.bumperAdsActivated = !!this.specificPlayerCfg?.bumperAdsEnabled && (window.ui.pageinfo.deviceclient === 'app' || this.videoduration < 40);
      this.log('bumper ads activated: ' + this.bumperAdsActivated);

      this.postRollsActivated = !this.bumperAdsActivated && !(this.curBrand === 'gmx' && this.videoduration <= 60);
      this.log('postrolls activated: ' + this.postRollsActivated);

      // PubSubs
      this.subscribe('video:pauseAllPlayers', () => {
        this.pauseAllPlayers();
      });
      this.subscribe('overlay:showWelcomeback', () => {
        this.pauseAllPlayers();
      });
      this.subscribe('searchbar:focus', () => {
        this.pauseAllPlayers();
      });
      this.subscribe('nav:opening', () => {
        this.pauseAllPlayers();
      });

      this.pageLoadedAsAResultOfRevolverAutoRedirect = this.detectIfComingFromRevolverAutoRedirect();

      // start player initialization
      this.initPlayer();
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Initialize the player:
   * - setup global player config
   * - lazyload jw player
   */
  initPlayer() {
    this.log('init player');
    void this.lazyloadJwPlayer();
  }

  /**
   * Lazyload JW player if not already done.
   */
  async lazyloadJwPlayer() {
    if (typeof window.jwplayer === 'function') {
      this.log('player already loaded, setup **');
      this.initAds();
    } else {
      this.log('load Player Script');
      await this.loadJwplayerScript();
    }
  }

  async loadJwplayerScript() {
    await Script.fetch(UrlPath.moduleAssets + '/newvideo/jwplayer.js').then(() => {
      this.log('player lazyloaded, set key');

      if (window.jwplayer) {
        window.jwplayer.key = this.licenceKey;
      }

      if (window.ui.adServiceEnabled) {
        this.log('adServiceEnabled');
        this.onAdServiceReady();
      } else {
        this.log('set up player');
        this.setUpPlayer();
      }
    });
  }

  onAdServiceReady() {
    this.subscribeOnce('ad:adServiceReady', () => {
      this.log('once: ad:adServiceReady');
      this.initAds();
    });

    // we need a ping-pong to know when adservice is initialized
    this.publish('mod:pingAdService', {});
    this.log('default: mod:pingAdService');
  }

  initAds() {
    this.log('initAds');

    // prebid timeout
    this.layoutClass = window.AdService.getParam('layoutclass');
    const asConfig = window.ui.AdServiceConfig;
    const configurations = asConfig?.config?.configurations;
    this.prebidTimeout.conf = configurations.prebidVideoTimeout
      ? configurations.prebidVideoTimeout[this.layoutClass] : 3000;

    this.log('current prebid timeout: ' + this.prebidTimeout.conf + ' ms');

    setTimeout(() => {
      this.prebidTimeout.triggered = true;
      this.log('prebid timeout triggered');
      if (!this.playerAlreadySetUp) {
        this.log('player still not set up, do it as fallback');
        this.setUpPlayer();
      }
    }, this.prebidTimeout.conf);

    this.setVastUrlConfig(this.bumperAdsActivated ? 'bumperad' : 'preroll');

    if (this.postRollsActivated) {
      this.setVastUrlConfig('postroll');
    }
  }

  /**
   * set VAST URL for pre- and postroll
   * Create vast with a combination of server-side params (vastParams) and adservice params
   */
  setVastUrlConfig(adtype: 'bumperad' | 'preroll' | 'postroll') {
    const tagid = 'v_' + adtype.toLowerCase();

    if (this.specificPlayerCfg?.vastConfig && window.AdService) {
      // Combine server-side params (meta-data from video-standalone page) with adservice params
      const vConf = this.specificPlayerCfg.vastConfig;

      const slot = window.AdService.AdSlot(tagid);

      // set additional configuration for pre and post rolls
      slot.setParam('portal', vConf.portal);
      slot.setParam('category', vConf.category);
      slot.setParam('section', vConf.section);
      slot.setParam('sectionlong', vConf.sectionlong);
      slot.setParam('categorytype', vConf.categorytype);
      slot.setParam('ngserious', vConf.ngserious);
      slot.setParam('agofid', vConf.agofid);
      slot.setParam('conpartner', vConf.conpartner);
      slot.setParam('layoutclass', this.layoutClass);
      slot.setParam('wpt', 'x');
      slot.setParam('pageview', this.getRenderingMode());
      // standalone or embed_top
      slot.setParam('integrationtype', this.videoPosition ?? '');
      // decision by TAM to default the parameter to empty, because it's set globally to 50 for Taboola:
      slot.setParam('special', vConf.special ?? '');

      // set additional homad if neccessary
      if (this.useHomad) {
        slot.setParam('homad', true);
      }

      // Specify whether the current video page has been loaded as a result of the auto-redirect from the Revolver module or not.
      if (this.pageLoadedAsAResultOfRevolverAutoRedirect) {
        slot.setParam('revolverplay', '1');
      } else {
        slot.setParam('revolverplay', '0');
      }

      // Get vast url for pre and postroll
      if (window.ui.AdServiceConfig.vastUrls) {
        // Used only for testing purposes.
        this.vastUrls = window.ui.AdServiceConfig.vastUrls;
      } else if (this.usePrebid) {
        window.AdService.getUrlForSlot(slot,
          adUrl => {
            this.storeVastUrl(adtype, adUrl);
          }
        );
      } else {
        const adUrl = window.AdService.getUrlForSlot(slot);
        this.storeVastUrl(adtype, adUrl);
      }
    }
  }

  @log()
  storeVastUrl(adType: 'preroll' | 'postroll' | 'bumperad', adUrl: string) {
    this.vastUrls[adType] = adUrl;

    if (this.prebidTimeout.triggered) {
      this.log('received ad url but timeout already triggered');
      return;
    }

    if (this.useHomad && !this.homadRequested) {
      // TODO: should it activate for bumper ads as well?
      if (this.vastUrls.preroll && (this.vastUrls.postroll || !this.postRollsActivated)) {
        void this.activateHomad();
      }
    }

    if (!this.playerAlreadySetUp && (adType === 'preroll' || adType === 'bumperad')) {
      this.setUpPlayer();
    }
  }

  /**
   * Ads need to know if the page has been loaded as a result of an auto-redirect coming from the Revolver module or not.
   * This information is stored by the Revolver module in a session storage entry, which will be read here and removed
   * so that the next time a video page loads by default it won't be because of a Revolver auto-redirect.
   */
  detectIfComingFromRevolverAutoRedirect() {
    if (window.sessionStorage?.getItem(autoRedirectStorageKey)) {
      window.sessionStorage?.removeItem(autoRedirectStorageKey);
      return true;
    }

    return false;
  }

  /**
   * get jwplayer rendering mode
   * returns either flash or html5
   */
  getRenderingMode(): string {
    return this.playerIsReady ? this.player.getProvider().name : 'html5';
  }

  /**
   * Whether the module is being displayed in fullscreen mode (either real or fake) or not.
   */
  isFullscreen() {
    return (document.fullscreenElement === this.root_) || this.isCustomFullscreen();
  }

  /**
   * Whether the module is being displayed in a custom fullscreen implementation.
   */
  isCustomFullscreen() {
    return this.root_.classList.contains('custom-fullscreen');
  }

  /**
   * set homad parameter and activate homad
   * Function provided by Schnee von Morgen embedded in HTML
   * will be called if useHomad is true
   */
  async activateHomad() {
    // check if homad lib already loaded
    try {
      this.homadRequested = true;

      if (typeof window.activateHomad !== 'function') {
        this.log('Loading homad lib');
        await Script.fetch(`${UrlPath.sn}/vendor/homad.min.js`);
        this.log('Homad lib loaded');
      }

      if (typeof window.activateHomad === 'function' && window.homadConfigs) {
        const cfgFileUrl = UrlPath.moduleAssets + '/newvideo/homadconfigs/'
          + this.curBrand + '-' + this.region + '.json';
        window.homadConfigs.clientConfig = cfgFileUrl;
        window.homadConfigs.prerollAdTag = this.vastUrls.preroll;
        window.homadConfigs.postrollAdTag = this.vastUrls.postroll;
        this.log('activating homad');
        window.activateHomad();
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * setup player
   */
  setUpPlayer() {
    try {
      this.log('setUpPlayer');

      this.playerAlreadySetUp = true;
      this.setPlayerConfig();

      if (this.playerId) {
        window.jwplayer(this.playerId).setup(this.globalPlayerConfig);

        this.player = window.jwplayer(this.playerId);

        if (!Viewport.isIOSApp()) {
          this.initCustomFullscreenFunctionality();

          this.root_.addEventListener('fullscreenchange', () => {
            if (document.fullscreenElement === this.root_) {
              this.root_.classList.add('native-fullscreen');
            } else {
              this.root_.classList.remove('native-fullscreen');
            }
          });
        }

        this.handleJwplayerEvents(this.playerId);

        const isStandalonePage = !!this.root_.closest('.standalone');

        if (isStandalonePage) {
          this.revolverRoot = this.root_.querySelector<HTMLElement>('.newvideo__revolver');

          if (this.revolverRoot) {
            import('./revolver').then(revolverModule => {
              if (this.revolverRoot) {
                this.initRevolver(revolverModule.Revolver, this.revolverRoot);
              }
            }).catch(e => this.logUnknownError(e, true));
          }
        }
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * merge specificplayer conf with global configs
   * used to set up player correctly
   */
  setPlayerConfig() {
    this.log('setup Player Config');

    // collect each playerId in one array
    if (!this.playerId) return;

    window.videoplayer.push(this.playerId);

    // set global config var
    this.globalPlayerConfig = this.getGlobalPlayerConfig();

    if (this.specificPlayerCfg?.vastConfig?.client && this.globalPlayerConfig.advertising) {
      this.globalPlayerConfig.advertising.client = this.specificPlayerCfg.vastConfig.client;
    } else if (this.globalPlayerConfig.advertising) {
      this.globalPlayerConfig.advertising.client = 'googima';
    }

    this.log(`Advertising client: ${this.globalPlayerConfig.advertising?.client}`);

    if (!this.specificPlayerCfg) return;

    // enable sticky video if floating is in the config object
    if (this.specificPlayerCfg.floating?.mode === 'never') {
      this.specificPlayerCfg.floating.mode = 'notVisible';
    }

    this.autoplay = this.specificPlayerCfg.autostart ?? false;

    // performance improvement
    // replace/use the loaded placeholder image instead of the image provided from the backend
    // to prevent additional network request
    const appliedImg = this.placeHolderImage?.querySelector<HTMLImageElement>('picture img');
    if (appliedImg?.currentSrc && this.specificPlayerCfg?.playlist) {
      this.specificPlayerCfg.playlist[0].image = appliedImg.currentSrc;
    }

    // merge globalConfig with specificConfig
    Object.entries(this.specificPlayerCfg).forEach(([key, value]) => {
      (this.globalPlayerConfig[key as keyof GlobalPlayerConfig] as unknown) = value;
    });

    console.log('[newvideo] global player config', this.globalPlayerConfig);
  }

  /**
   * Create initial global player configuration object.
   */
  getGlobalPlayerConfig(): GlobalPlayerConfig {
    return {
      logo: {
        hide: true,
        link: 'https://web.de/'
      },
      skin: {
        name: 'seven'
      },
      advertising: {
        client: 'googima',
        admessage: 'Werbung endet in xx Sekunden',
        cuetext: 'Werbung',
        maxRedirects: 10
      },
      localization: {
        nextUp: 'nächstes Video',
        playlist: 'Playlist',
        related: 'verwandte Videos',
        loadingAd: 'Werbung lädt',
        liveBroadcast: 'Live Übertragung',
        replay: 'wiederholen',
        buffer: 'lädt'
      },
      preload: 'auto',
      base: UrlPath.moduleAssets + '/newvideo/',
      width: '100%',
      aspectratio: '16:9',
      autostart: true,
      mute: false,
      stretching: 'uniform',
      visualplaylist: false, // hide built-in playlist
      videoposition: 'test_position',
      duration: 1337,
      bumperAdsEnabled: false
    };
  }

  /**
   * Initializes Revolver.
   * @param revolverClass
   * @param revolverRoot
   */
  initRevolver(revolverClass: typeof Revolver, revolverRoot: HTMLElement) {
    this.revolver = new revolverClass(revolverRoot, this.currentCoremId, this.componentPath);
    this.revolver.init(this.root_.dataset.recommendnext ?? '');

    this.root_.classList.add('revolver-enabled');

    PubSub.subscribe('revolver:toggle-fullscreen', () => {
      this.toggleFullscreen().then(() => {
        const isFullscreen = this.isFullscreen();

        Tracking2.callTealiumLink({
          businessEventType: 'userAction',
          componentPath: `${this.componentPath}.revolver.fullscreen-icon`,
          componentName: 'video.revolverplay',
          componentResultState: isFullscreen ? 'expanded' : 'minimized' // Fullscreen API is asynchronous, so we track the opposite state of what it was before toggling fullscreen.
        });

        if (isFullscreen) {
          Tracking2.callTealiumLink({
            businessEventType: 'expose',
            componentPath: `${this.componentPath}.revolver.fullscreen`,
            componentName: 'video.revolverplay'
          });
        }
      }).catch(e => this.logUnknownError(e, true));
    });

    PubSub.subscribe('revolver:close', () => {
      if (this.revolverRoot) {
        this.revolverRoot.style.display = 'none';
      }

      if (this.resumePlaybackAfterClosingRevolver) {
        this.player.play();
        this.resumePlaybackAfterClosingRevolver = false;
      }
    });

    // Show more videos button
    this.player.addButton(
      UrlPath.moduleAssets + '/newvideo/icon-more-videos.svg',
      'Weitere Videos anzeigen', // Accessible tooltip
      () => {
        try {
          if (this.eventPhase === 'running') {
            this.resumePlaybackAfterClosingRevolver = true;
          }
          this.showRevolver('more-videos-button', false);
          this.trackUserAction('.revolver-button');
        } catch (e) {
          this.logUnknownError(e);
        }
      },
      'newvideo__more-videos-button',
      'newvideo__more-videos-button'
    );
  }

  /**
   * Displays the Revolver popup.
   */
  showRevolver(trigger: RevolverTrigger, countdownTimerEnabled = true) {
    if (!this.revolver) return;

    this.pauseAllPlayers();

    setTimeout(() => {
      if (this.revolverRoot) {
        this.revolverRoot.style.display = 'block';
      }

      this.revolver.showPopup(trigger, countdownTimerEnabled, this.getBrainVidUrl());

      if (Viewport.isIOSApp() && this.isFullscreen()) this.revolver.pauseTimer();
    }, 200);
  }

  /**
   * get the BrainVidUrl if its part of the config
   * returns String with brainVidUrl
   */
  getBrainVidUrl() {
    this.brainVidUrl = '?';
    if (this.specificPlayerCfg && !!this.specificPlayerCfg.brainVidUrl) {
      this.brainVidUrl = atob(this.specificPlayerCfg.brainVidUrl);
    }
    return this.brainVidUrl;
  }

  /**
   * Initializes a custom full screen functionality instead of relying on the one provided by JWPlayer.
   */
  private initCustomFullscreenFunctionality() {
    this.player.setAllowFullscreen(false);

    this.player.addButton(UrlPath.moduleAssets + '/newvideo/open_fullscreen-white.svg', 'Vollbild',
      () => {
        this.toggleFullscreen().catch(e => this.logUnknownError(e, true));
      },
      'fullscreenBtn',
      'newvideo__fullscreen-button'
    );

    this.root_.addEventListener('keydown', e => {
      if (e.key === 'f') {
        this.toggleFullscreen().catch(e => this.logUnknownError(e, true));
      } else if (e.key === 'Escape') {
        if (this.isCustomFullscreen()) {
          this.exitCustomFullscreen();
        }
      }
    });

    this.root_.addEventListener('dblclick', e => {
      if ((e.target as HTMLElement).tagName === 'VIDEO') {
        this.toggleFullscreen().catch(e => this.logUnknownError(e, true));
      }
    });
  }

  /**
   * https://docs.jwplayer.com/players/docs/jw8-player-events-reference
   */
  handleJwplayerEvents(playerId: string) {
    this.log('bind all PLayer-Events for player: ' + playerId);

    // Setup Events
    this.player.on('ready', () => {
      this.playerIsReady = true;
      this.onReady();
    });
    this.player.on('setupError', (e: PlayerErrorEvent) => {
      this.handleOnSetupError(e);
    });

    // this event is a suitable place to handle cases where autoplay is blocked by the browser
    this.player.on('autostartNotAllowed', () => {
      this.autoplay = false;
      this.log('autoplay with sound not allowed, try muted');
      const isStandalonePage = !!this.root_.closest('.standalone');
      if (isStandalonePage) {
        this.autoplayMuted = true;
        this.mutedProgrammatically = true;
        this.player.setMute(true);
        this.player.play();
      }
    });

    // Playlist Events
    this.player.on('playlistItem', () => {
      this.playbackStarted = false;
    });

    // Playback Events
    this.player.on('play', (e: PlaybackEvent) => {
      this.onPlay(e);
    });
    this.player.on('pause', () => {
      this.onPause();
    });
    this.player.on('complete', () => {
      this.onComplete();
    });
    this.player.on('firstFrame', () => {
      this.onFirstFrame();
    });
    this.player.on('error', (e: PlayerErrorEvent) => {
      this.onError(e);
    });
    this.player.on('time', (e: PlayerEvent) => {
      this.onTime(e);
    });
    this.player.on('float', (e: PlayerEvent) => {
      this.onFloat(e);
    });
    this.player.on('mute', (e: PlayerEvent) => {
      this.onMute(e);
    });

    this.player.on('fullscreen', (e: FullscreenEvent) => {
      this.onFullScreen(e);
    });

    this.player.on('seeked', () => {
      this.onSeeked();
    });

    this.player.on('seek', (e: PlayerEvent) => {
      this.onSeek(e);
    });

    // AdEvents
    this.player.on('adClick', (e: PlayerEvent) => {
      this.onAdClick(e);
    });
    this.player.on('adCompanions', (e: PlayerEvent) => {
      this.onAdCompanions(e);
    });
    this.player.on('adComplete', (e: PlayerEvent) => {
      this.onAdComplete(e);
    });
    this.player.on('adSkipped', () => {
      this.onAdSkipped();
    });
    this.player.on('adError', (e: AdErrorEvent) => {
      this.onAdError(e);
    });
    this.player.on('adLoadedXML', (e: AdLoadedXMLEvent) => {
      this.onLoadXML(e);
    });
    this.player.on('adPlay', () => {
      this.onAdPlay();
    });
    this.player.on('adRequest', (e: AdEvent) => {
      console.log('[newvideo] adRequest', e);
    });
    this.player.on('adImpression', (e: PlayerEvent) => {
      this.onAdImpression(e);
    });
    this.player.on('adTime', (e: PlayerEvent) => {
      this.onAdTime(e);
    });
    this.player.on('beforePlay', (e: BeforePlayEvent) => {
      this.onBeforePlay(e);
    });
    this.player.on('beforeComplete', () => {
      this.onBeforeComplete();
    });
  }

  onSeeked() {
    if (this.componentPath && this.videoPosition) {
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentName: 'video',
        componentPath: `${this.componentPath}.progress-bar`,
        elementTagName: 'div',
        eventType: 'mousedown',
        videoInfo: this.getVideoInfo(this.playerCurrentTime)
      });
    }
  }

  onSeek(e: PlayerEvent) {
    this.playerCurrentTime = e.position;
  }

  onFullScreen(e: FullscreenEvent) {
    if (!e.fullscreen && Viewport.isIOSApp()) {
      this.revolver.resumeTimer();
    }

    this.trackFullscreenEvent(e.fullscreen);
  }

  enterCustomFullscreen() {
    try {
      this.log('enter custom fullscreen');

      this.pageScrollTop = document.documentElement.scrollTop;

      document.body.style.backgroundColor = '#1E1E25'; // Must be the same as .jw-wrapper element

      findAndHideElement('.wrapper');
      findAndHideElement('[data-mod-name="infinitescroll"]');
      findAndHideElement('aside.left-col');
      findAndHideElement('[data-mod-name="header"]');
      findAndHideElement('.additionalArticles');
      findAndHideElement('.standalone-video__body');

      this.customFullscreenPlaceholder = document.createElement('div');
      this.customFullscreenPlaceholder.className = 'revolver__initial-position-placeholder';
      this.root_.replaceWith(this.customFullscreenPlaceholder);
      this.root_.classList.add('custom-fullscreen');

      document.body.appendChild(this.root_);

      function findAndHideElement(elementSelector: string) {
        const element = document.querySelector<HTMLElement>(elementSelector);
        if (element) {
          element.dataset.originalDisplayStyle = element.style.display;
          element.style.display = 'none';
        }
      }
    } catch (e) {
      this.logUnknownError(e, true);
      this.exitCustomFullscreen();
    }
  }

  exitCustomFullscreen() {
    try {
      if (!this.isCustomFullscreen()) return;

      this.log('exit custom fullscreen');

      document.body.style.removeProperty('background-color');

      this.root_.classList.remove('custom-fullscreen');

      findAndShowElement('.wrapper');
      findAndShowElement('[data-mod-name="infinitescroll"]');
      findAndShowElement('aside.left-col');
      findAndShowElement('[data-mod-name="header"]');
      findAndShowElement('.additionalArticles');
      findAndShowElement('.standalone-video__body');

      // restore previous position
      if (this.customFullscreenPlaceholder) this.customFullscreenPlaceholder.replaceWith(this.root_);

      document.documentElement.scroll({
        top: this.pageScrollTop,
        behavior: 'instant'
      });

      function findAndShowElement(elementSelector: string) {
        const element = document.querySelector<HTMLElement>(elementSelector);
        if (element) {
          if (element.dataset.originalDisplayStyle) {
            element.style.display = element.dataset.originalDisplayStyle;
          } else {
            element.style.removeProperty('display');
          }
        }
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Toggles fullscreen mode.
   */
  private async toggleFullscreen() {
    try {
      if (this.isFullscreen()) {
        if (!isFullscreenAPIAvailableForElements()) {
          this.exitCustomFullscreen();
          this.trackFullscreenEvent(false);
        } else {
          await document.exitFullscreen()
            .then(() => {
              this.trackFullscreenEvent(false);
            })
            .catch(e => {
              this.logUnknownError(e, true);
              this.exitCustomFullscreen();
              this.trackFullscreenEvent(false);
            });
        }
      } else if (isFullscreenAPIAvailableForElements()) {
        await this.root_.requestFullscreen()
          .then(() => {
            this.trackFullscreenEvent(true);
          })
          .catch(err => {
            console.log('Native fullscreen failed:', err);

            this.enterCustomFullscreen();
            this.trackFullscreenEvent(true);
          });
      } else {
        console.log('Fullscreen API not supported on this device');
        this.enterCustomFullscreen();
        this.trackFullscreenEvent(true);
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /**
   * Tracks full screen events.
   */
  private trackFullscreenEvent(fullscreen: boolean) {
    if (!this.componentPath || !this.videoPosition) return;
    const componentPath = fullscreen ? `${this.componentPath}.fullscreen-icon` : `${this.componentPath}.fullscreen.fullscreen-icon`;

    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentName: 'video',
      componentPath,
      componentResultState: fullscreen ? 'enlarged' : 'minimized',
      elementTagName: 'div',
      eventType: 'mousedown',
      videoInfo: this.getVideoInfo(this.playerCurrentTime)
    });

    this.fullScreen = fullscreen;
    this.expanded = fullscreen;

    if (fullscreen) {
      Tracking2.callTealiumLink({
        businessEventType: 'expose',
        componentName: 'video',
        componentPath: `${this.componentPath}.fullscreen`,
        elementTagName: 'div',
        videoInfo: this.getVideoInfo()
      });
    }
  }

  /**
   * Pause all players found within window.videoplayer array
   */
  pauseAllPlayers() {
    this.log('pause All Players');
    window.videoplayer.forEach(e => {
      const player = window.jwplayer(e);

      if (player) {
        player.pause(true);
        this.log('pause Player: ' + e);
      }
    });
  }

  /**
   * Pause all other Players found within window.videoplayer Array
   */
  pauseOtherPlayers() {
    this.log('pause Other Players');
    window.videoplayer.forEach((playerID: string) => {
      if (this.playerId && playerID !== this.playerId) {
        window.jwplayer(playerID).pause(true);

        this.log('pause Player: ' + playerID);
      }
    });
  }

  /**
   * Handle custom error messages
   */
  createCustomError(errorMsg: string) {
    if (!this.playerId) return;

    const playerNode = document.getElementById(this.playerId);

    const errorWrapperNode = document.createElement('div');
    const errorFlexContainer = document.createElement('div');
    const errorMsgNode = document.createElement('div');

    errorWrapperNode.setAttribute('class', 'errorWrapper ratio16_9');
    errorFlexContainer.setAttribute('class', 'errorContainer');
    errorMsgNode.setAttribute('class', 'errorMessage content-padding');

    errorMsgNode.innerText = errorMsg;
    errorWrapperNode.appendChild(errorFlexContainer);
    errorFlexContainer.appendChild(errorMsgNode);
    this.root_.appendChild(errorWrapperNode);

    if (playerNode?.parentNode) {
      playerNode.parentNode.removeChild(playerNode);
    }

    this.log('createCustomError' + errorMsg);
  }

  /**
   * play specific ad
   */
  playAdType(adType: 'preRoll' | 'postRoll' | 'bumperad') {
    try {
      const totalAdPlayed = this.adsPlayedByType[adType];

      // Do not play more than two ads for bumper, or one ad for all other types.
      if (this.bumperAdsActivated) {
        if (totalAdPlayed >= 2) return;
      } else if (totalAdPlayed >= 1) {
        return;
      }

      if (totalAdPlayed == 0) {
        this.setPi('adcall-' + adType);
      } else {
        this.setPi('adcall-' + adType + '_' + (totalAdPlayed + 1));
      }

      if (adType === 'bumperad') {
        this.trackTechnicalVideoEvent(`adcall.bumperad-${totalAdPlayed + 1}`);
      } else {
        this.trackTechnicalVideoEvent(`adcall.${adType}`);
      }

      const adUrl = new URL(this.vastUrls[adType.toLowerCase() as keyof typeof this.vastUrls], window.location.origin);

      // Set autoplay parameter
      adUrl.searchParams.set('prf[autoplay]', this.autoplay || this.autoplayMuted ? '1' : '0');
      // Set muted parameter
      adUrl.searchParams.set('prf[muted]', this.muted ? '1' : '0');

      this.player?.loadAdTag(adUrl.toString());

      this.adsPlayedByType[adType] += 1;
      this.lastAdPlayedFormat = adTypeMap[adType];

      this.log(`to play ${adType}: ${adUrl}`);
    } catch (e) {
      this.publish('sentry:log', {
        error: e,
        module: 'newvideo'
      });
    }
  }

  /**
   * Compose and publish a PI towards analytics.
   * Example: this.setPi('action=what', {'click1': 'foo', 'click2': 'bar', 'newParam': 'hehe'});
   */
  setPi(videoaction: string, additionalParam?: TrackingClickData) {
    const pixelObj: TrackingData = {
      piUrl: this.getBrainVidUrl(),
      autoplay: this.autoplay,
      referrer: '&referrer=undefined',
      click1: '&click1=undefined',
      click2: '&click2=undefined',
      click3: '&click3=undefined',
      click4: '&click4=undefined',
      videoaction: '&videoaction=' + videoaction,
      videoposition: this.videoPosition,
      videoduration: this.videoduration
    };

    if (typeof additionalParam !== 'undefined') {
      pixelObj.click1 = additionalParam.click1;
      pixelObj.click2 = additionalParam.click2;
      pixelObj.click3 = additionalParam.click3;
      pixelObj.click4 = additionalParam.click4;
    }

    this.publish('video:setPixel', pixelObj);
  }

  /**
   * Tracks a user action.
   */
  trackUserAction(componentPath = '') {
    if (this.componentPath) {
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentName: 'video',
        componentPath: componentPath ? `${this.componentPath}${componentPath}` : this.componentPath,
        eventType: 'click',
        videoInfo: this.getVideoInfo(this.playerCurrentTime)
      });
    }
  }

  /**
   * Tracks a video event.
   */
  trackVideoEvent(videoEventType: videoEventType, adInfo?: AdInfo) {
    if (!this.componentPath) return;

    const eventData = {
      businessEventType: 'videoplay',
      componentName: 'video',
      componentPath: `${this.componentPath}`,
      videoInfo: this.getVideoInfo(undefined, videoEventType)
    } as EventData;

    if (adInfo) {
      eventData.adInfo = adInfo;
    }

    console.log('[newvideo] trackVideoEvent', videoEventType, eventData);

    Tracking2.callTealiumLink(eventData);
  }

  trackTechnicalVideoEvent(name?: string, errorType?: string) {
    if (this.componentPath) {
      Tracking2.callTealiumLink({
        businessEventType: 'technical.event',
        componentName: 'video',
        componentPath: `${this.componentPath}`,
        technicalEventInfo: {
          name: name,
          errorType: errorType,
          requestedService: 'jwplayer'
        },
        videoInfo: this.getVideoInfo(this.playerCurrentTime)
      });
    }
  }

  /** Tracks when the playback starts only if it hasn't started already. */
  trackPlaybackStart() {
    if (!this.playbackStarted) {
      this.playbackStarted = true;
      this.setPi('video-playback-start');
      this.trackTechnicalVideoEvent('videoplayer.start');
    }
  }

  /**
   * helper function to publish to new & old static
   */
  publishAndMsg(publishName: string, additionalParam?: Record<string, string>) {
    const additional = additionalParam ?? undefined;
    this.publish(publishName, additional);
  }

  /**
   * Signifies when the player has been initialized in either Flash or HTML5 and is ready for playback
   */
  onReady() {
    this.log('onReady');

    const renderingMode = this.player.getProvider().name;
    let settingsOpened = false;

    // Tracking
    this.publish('analytics:trackVideoEvent', 'Video Event "Ready"');
    this.publish('analytics:trackVideoEvent', 'Video Event "RenderingMode": ' + renderingMode);
    this.setPi('video-start');

    if (this.componentPath) {
      Tracking2.callTealiumLink({
        businessEventType: 'expose',
        componentName: 'video',
        componentPath: `${this.componentPath}`,
        elementTagName: 'div',
        videoInfo: this.getVideoInfo(this.playerCurrentTime)
      });
    }

    // remove placeholder image after player has been set up
    this.placeHolderImage?.remove();

    // listen for picture in picture
    const jwPlayerContainer = this.player.getContainer();

    const jwPlayerVideoElement = jwPlayerContainer.querySelector('video');
    jwPlayerVideoElement?.addEventListener('enterpictureinpicture', () => {
      if (!this.componentPath || !this.videoPosition) return;
      Tracking2.callTealiumLink({
        businessEventType: 'userAction',
        componentName: 'video',
        componentPath: `${this.componentPath}.browser-expand-icon`,
        componentResultState: 'extended',
        elementTagName: 'div',
        eventType: 'mousedown',
        videoInfo: this.getVideoInfo(this.playerCurrentTime)
      });

      this.expanded = true;

      Tracking2.callTealiumLink({
        businessEventType: 'expose',
        componentName: 'video',
        componentPath: `${this.componentPath}.browser-expand`,
        elementTagName: 'div',
        videoInfo: this.getVideoInfo()
      });
    });

    jwPlayerVideoElement?.addEventListener('leavepictureinpicture', () => {
      this.expanded = false;
    });

    const jwPlayerSettingsIcon = jwPlayerContainer.querySelector('.jw-settings-submenu-button');
    jwPlayerSettingsIcon?.setAttribute('tabindex', '0');

    const closeSettings = (e: Event) => {
      const target = e.target as HTMLElement;
      const parent = target.parentElement;
      if ((target && target.getAttribute('aria-label') !== 'Einstellungen') && (parent && parent.getAttribute('aria-label') !== 'Einstellungen')) {
        settingsOpened = false;
      }

      window.removeEventListener('click', closeSettings);
    };

    jwPlayerSettingsIcon?.addEventListener('click', () => {
      if (this.componentPath && this.videoPosition && !settingsOpened) {
        Tracking2.callTealiumLink({
          businessEventType: 'userAction',
          componentName: 'video',
          componentPath: `${this.componentPath}.settings-icon`,
          elementTagName: 'div',
          eventType: 'mousedown',
          videoInfo: this.getVideoInfo(this.playerCurrentTime)
        });
        window.addEventListener('click', closeSettings);
      }

      settingsOpened = !settingsOpened;
    });

    const jwPlayerPlayIcon = jwPlayerContainer.querySelector('div.jw-controlbar .jw-icon-playback');
    jwPlayerPlayIcon?.addEventListener('click', () => {
      const status = jwPlayerPlayIcon.getAttribute('aria-label') as 'Spielen' | 'Pause' | null;
      if (!status) return;
      if (this.componentPath && this.videoPosition) {
        Tracking2.callTealiumLink({
          businessEventType: 'userAction',
          componentName: 'video',
          componentPath: `${this.componentPath}.play-icon`,
          componentResultState: status == 'Spielen' ? 'played' : 'paused',
          eventType: 'mousedown',
          elementTagName: 'div',
          videoInfo: this.getVideoInfo(this.playerCurrentTime)
        });
      }
    });

    jwPlayerContainer?.addEventListener('click', e => {
      const target = e.target as HTMLElement;

      if (target && (target instanceof HTMLVideoElement || target.getAttribute('aria-label') == 'lädt')) {
        const isPlayerPausedState = jwPlayerContainer.querySelector('.jw-display-controls [aria-label=Pause]');

        if (this.componentPath && this.videoPosition) {
          setTimeout(() => {
            Tracking2.callTealiumLink({
              businessEventType: 'userAction',
              componentName: 'video',
              componentPath: `${this.componentPath}.video-area`,
              componentResultState: !isPlayerPausedState ? 'played' : 'paused',
              eventType: 'mousedown',
              elementTagName: 'div',
              videoInfo: this.getVideoInfo(this.playerCurrentTime)
            });
          }, 3000);
        }
      }
    });
  }

  /**
   * Fired when a media error has occurred, causing the player to stop playback and go into idle mode.
   */
  onError(e: PlayerErrorEvent) {
    // Show custom error message if LiveStream has ended or hasn't started yet
    if (e.message === 'Cannot load M3U8: 404 Not Found') {
      this.createCustomError('Dieser Live-Stream ist derzeit nicht verfügbar oder wurde bereits beendet.');
      this.log('Livestream beended');
    } else {
      this.createCustomError('Aufgrund eines technischen Problems kann dieses Video derzeit leider nicht abgespielt werden.');
    }

    // Tracking
    this.setPi('video-error');
    this.trackTechnicalVideoEvent(undefined, 'videoplayer.error');

    this.publish('analytics:trackVideoEvent', 'Video Event "onError" - error: ' + e.message);
    this.publish('sentry:log', {
      error: e,
      module: 'newvideo'
    });

    this.logError('Error' + e.message);
  }

  /**
   * Fired when neither the Flash nor HTML5 player could be set up.
   */
  handleOnSetupError(e: PlayerErrorEvent) {
    this.publish('analytics:trackVideoEvent', 'Video Event "onSetupError"');

    this.setPi('setup-error');
    this.trackTechnicalVideoEvent(undefined, 'videoplayer.setuperror');

    this.publish('sentry:log', {
      error: e,
      module: 'newvideo'
    });
    this.logError('Setup error');
  }

  /**
   * Fired when the playlist index changes to a new playlist item.
   * This event occurs before the player begins playing the new playlist item.
   */
  onPlay(e: PlaybackEvent) {
    this.log('onPlay');
    this.publish('analytics:trackVideoEvent', 'Video Event "onPlay" - oldstate: ' + e.oldstate);
    this.pauseOtherPlayers();
    this.setPi('video-play-' + e.playReason);
    this.publishAndMsg('welcomeback:stopWBTimer');
    this.eventPhase = 'running';
  }

  /**
   * Fired when the player enters the paused state.
   */
  onPause() {
    this.log('onPause');
    this.publish('welcomeback:restartWBTimer', {});
    this.eventPhase = 'paused';
  }

  /**
   * Fired when an item completes playback.
   * no return value / callback
   */
  onComplete() {
    this.log('onComplete');
    this.setPi('video-end');
    this.eventPhase = 'finished';
    this.showRevolver('autoplay');
    this.trackVideoEvent('end');
    this.publish('analytics:trackVideoEvent', 'Video Event "onComplete"');
    this.publishAndMsg('welcomeback:restartWBTimer');
  }

  /**
   * This event pinpoints when content playback begins.
   * Use this to determine the period of time between a user pressing play and the same user viewing their content
   */
  onFirstFrame() {
    this.log('onFirstFrame');
    this.setPi('content-start');
    this.trackVideoEvent('start');
    this.trackPlaybackStart();
    this.publishAndMsg('welcomeback:stopWBTimer');
    this.adjustFullscreenButton();
    this.adjustMoreVideosButton();
  }

  /**
   * VAST and IMA. Fired whenever a user clicks an ad to be redirected to its landing page.
   */
  onAdClick(e: PlayerEvent) {
    this.log('onAdClick');
    this.publish('analytics:trackVideoEvent', 'Video Event "onAdClick" - tag: ' + e.tag);
  }

  /**
   * VAST and IMA. Fired whenever an ad contains companions.
   */
  onAdCompanions(e: PlayerEvent) {
    this.log('onAdCompanions');
    this.publish('analytics:trackVideoEvent', 'Video Event "onAdCompanions" - tag: ' + e.tag);
  }

  /**
   * VAST and IMA. Fired whenever an ad has completed playback.
   */
  onAdComplete(e: PlayerEvent) {
    this.log('onAdComplete: ' + e.tag);
    this.publish('analytics:trackVideoEvent', 'Video Event "onAdComplete" - tag: ' + e.tag);
    this.trackVideoEvent('adEnd', this.getAdInfo());

    // Check if we need to play a second bumper ad.
    if (this.bumperAdsActivated && this.adsPlayedByType.bumperad < 2 && this.adDuration < 8) {
      this.log('play second bumper ad');
      this.playAdType('bumperad');
    }
  }

  /**
   * VAST and IMA. Fired whenever an ad has been skipped.
   */
  onAdSkipped() {
    this.publish('analytics:trackVideoEvent', 'Video Event "onAdSkipped"');
    this.log('onAdSkipped');
  }

  /**
   * VAST and IMA. Fired whenever an error prevents the ad from playing.
   */
  onAdError(e: AdErrorEvent) {
    this.publish('analytics:trackVideoEvent',
      'Video Event "onAdError" - message: ' + e.message + ' || tag: ' + e.tag);

    const videoAction = e?.tag?.includes('v_preroll') ? 'adcall-error_preRoll'
      : e?.tag?.includes('v_postroll') ? 'adcall-error_postRoll'
        : e?.tag?.includes('v_bumperad') ? 'adcall-error_bumperad'
          : 'adcall-error';

    this.setPi(`${videoAction}|errorCode=${e.code}|adErrorCode=${e.adErrorCode}|message=${e.message}|tag=${e.tag}`);
    this.trackTechnicalVideoEvent(undefined, videoAction);

    const sentryObj = {
      adErrorCode: e.adErrorCode,
      adId: e.adId ?? '',
      adposition: e.adposition,
      errorCode: e.code,
      message: e.message,
      responseText: (e.request?.response ?? e.request?.responseText) ?? '',
      tag: e.tag,
      timeLoading: e.timeLoading,
      timeout: e.timeout,
      wrappedTags: e.wrappedTags ?? [],
      wrappedAdSystem: e.wrapperAdSystem ?? [],
      creativeid: this.creativeid ?? 'No Response Data Available'
    };

    this.publish('sentry:log', {
      error: sentryObj,
      module: 'newvideo'
    });

    console.log('[newvideo] onAdError', e, sentryObj);
  }

  /**
   * VAST and IMA. Fired whenever an XML is being loaded included all the redirects.
   */
  onLoadXML(e: AdLoadedXMLEvent) {
    if (e.tag.includes('adition.com')) {
      for (const xmlElem of e.xml.all) {
        if (xmlElem instanceof Element && xmlElem.id) {
          this.creativeid = xmlElem.id;
        }
      }
    }
  }

  /**
   * Fired whenever an ad starts playing or when an ad is unpaused.
   */
  onAdPlay() {
    this.log('onAdPlay');
    this.trackPlaybackStart();
    this.publish('analytics:trackVideoEvent', 'Video Event "onAdPlay"');
    // CADNPCA-9434 in case user jumped away while playing ad, the CCR should also be sent with value 0
    PubSub.publish('content:recoCCR', {progress: 0, contentType: window.ui.recom?.contentType});
  }

  /**
   * VAST and IMA. Fired based on the IAB definition of an ad impression.
   * This occurs the instant a video ad begins to play.
   */
  onAdImpression(e: PlayerEvent) {
    this.log('onAdImpression');
    this.publish('analytics:trackVideoEvent', 'Video Event "onAdImpression"- tag: ' + e.tag);
    this.setPi('video-start-ad');
    this.trackVideoEvent('adStart', this.getAdInfo());
  }

  /**
   * Fired while ad playback is in progress.
   */
  onAdTime(e: PlayerEvent) {
    this.publish('analytics:trackVideoEvent',
      'Video Event "onAdTime" - position: ' + e.position + ' / duration:' + e.duration);

    if (this.adDuration === 0) {
      this.adDuration = e.duration;

      if (this.bumperAdsActivated && e.duration < 8) {
        window.AdService.getUrlForSlot(window.AdService.AdSlot('v_bumperad'),
          adUrl => {
            this.log('async adUrl from AdService (bumper ad #2): ' + adUrl);
            this.vastUrls.bumperad = adUrl;
          }
        );
      }
    }
  }

  /**
   * Fired just before the player begins playing.
   * Unlike the onPlay event, the player will not have begun playing or buffering when triggered,
   * which makes this the right moment to insert preroll ads using loadAdTag(tag).
   * no return value / callback
   */
  onBeforePlay(e: BeforePlayEvent) {
    this.log('onBeforePlay');

    const eObject = {
      playReason: e.playReason,
      startTime: e.startTime || '',
      type: e.type || '',
      viewable: e.viewable || '',
      state: e.state || ''
    };

    this.setPi(`before-play|playReason=${eObject.playReason}|startTime=${parseInt(eObject.startTime)}|type=${eObject.type}|viewable=${eObject.viewable}|state=${eObject.state}`);
    this.publish('analytics:trackVideoEvent', 'Video Event "onBeforePlay"');

    // Ensure preroll or bumper ads are requested only if the video hasn't started playing yet.
    if (this.eventPhase === 'ready') {
      if (this.bumperAdsActivated) {
        this.playAdType('bumperad');
      } else {
        this.playAdType('preRoll');
      }
    }
  }

  /**
   * Fired just before the player completes playing.
   * Unlike the onComplete event, the player will not have moved on to either showing the replay screen
   * or advancing to the next playlistItem, which makes this the right moment to insert post-roll ads using loadAdTag(tag).
   * no return value / callback
   */
  onBeforeComplete() {
    this.log('onBeforeComplete');
    this.publish('analytics:trackVideoEvent', 'Video Event "onBeforeComplete"');
    this.setPi('content-end');
    this.trackVideoEvent('contentView_100');

    if (this.postRollsActivated) this.playAdType('postRoll');
  }

  /**
   * Track the player progress (for displaying revolver popup etc.)
   */
  onTime(e: PlayerEvent) {
    this.playerCurrentTime = e.position;

    if (!this.watched0) {
      this.trackVideoEvent('contentView_0');
      this.watched0 = true;
    }

    // tracking for 25%, 50% or 75% watched
    if (!this.watched25 && e.position >= ((e.duration * 25) / 100)) {
      this.setPi('content-25-watched');
      this.trackVideoEvent('contentView_25');
      this.watched25 = true;
    }
    if (!this.watched50 && e.position >= ((e.duration * 50) / 100)) {
      this.setPi('content-50-watched');
      this.trackVideoEvent('contentView_50');
      this.watched50 = true;
      this.showMoreVideosToast();
    }
    if (!this.watched75 && e.position >= ((e.duration * 75) / 100)) {
      this.setPi('content-75-watched');
      this.trackVideoEvent('contentView_75');
      this.watched75 = true;
    }
    // this.log('onTime ');

    // CADNPCA-9434 track video completion rate
    const compRate = e.position / e.duration;
    PubSub.publish('content:recoCCR', {progress: compRate, contentType: window.ui.recom?.contentType});
  }

  /**
   * Fired when video gets sticky/unsticky
   */
  onFloat(e: PlayerEvent) {
    this.floating = e.floating;
    this.expanded = e.floating;

    if (e.floating) {
      this.setPi('video-sticky');
      if (this.componentPath && this.videoPosition) {
        Tracking2.callTealiumLink({
          businessEventType: 'expose',
          componentName: 'video',
          componentPath: `${this.componentPath}.sticky-video`,
          elementTagName: 'div',
          videoInfo: this.getVideoInfo()
        });
      }
    }
  }

  /**
   * Fired when video gets muted/unmuted
   */
  onMute(e: PlayerEvent) {
    if (!this.mutedProgrammatically) {
      if (e.mute) {
        this.setPi('video-mute');
      } else {
        this.setPi('video-unmute');
      }

      if (this.componentPath && this.videoPosition) {
        Tracking2.callTealiumLink({
          businessEventType: 'userAction',
          componentName: 'video',
          componentPath: `${this.componentPath}.mute-icon`,
          componentResultState: e.mute ? 'muted' : 'unmuted',
          eventType: 'mousedown',
          elementTagName: 'div',
          videoInfo: this.getVideoInfo(this.playerCurrentTime)
        });
      }
    }

    this.muted = e.mute;

    // Reset to clean up the state for successive events.
    this.mutedProgrammatically = false;
  }

  /**
   * Returns an object containing ad information from an ad-related player event.
   */
  getAdInfo(): AdInfo {
    return {
      format: this.lastAdPlayedFormat,
      property: 'video'
    };
  }

  getVideoInfo(currentTime?: number, eventType?: videoEventType): VideoInfo {
    const getVideoPosition = () => {
      // do floating check first because when floating(sticky) it is also expanded
      // But when expanded it is not floating
      if (this.floating) {
        return 'sidebar-left';
      } else if (this.expanded) {
        return 'browser-overlay';
      } else {
        return this.videoPosition;
      }
    };

    return {
      articleId: this.articleId,
      contentType: this.contentType,
      duration: this.videoduration,
      position: getVideoPosition(),
      provider: this.videoProvider,
      teaserHeadline: this.specificPlayerCfg?.teaserHeadline ?? '',
      videoPlayer: 'JWP',
      videoState: {
        currentTime: currentTime,
        videoEventType: eventType,
        properties: {
          autoplay: this.autoplay,
          autoplayMuted: this.autoplayMuted,
          fullscreen: this.fullScreen,
          expanded: this.expanded,
          muted: this.muted,
          sticky: this.floating
        },
        phase: this.eventPhase
      }
    };
  }

  adjustFullscreenButton() {
    // Move the fullscreen button to the end to allow for correct tab order.
    const fullscreenButton = this.player.getContainer().querySelector('.newvideo__fullscreen-button');
    if (fullscreenButton) fullscreenButton.parentElement?.appendChild(fullscreenButton);
  }

  adjustMoreVideosButton() {
    const moreVideosButton = this.player.getContainer().querySelector<HTMLElement>('.newvideo__more-videos-button');

    if (!moreVideosButton) return;

    // Add accessibility attributes
    moreVideosButton.setAttribute('role', 'button');
    moreVideosButton.setAttribute('aria-label', 'Weitere Videos');

    // Add text element
    const moreVideosLabel = document.createElement('span');
    moreVideosLabel.innerText = 'Weitere Videos';
    moreVideosLabel.className = 'newvideo__more-videos-label';
    moreVideosLabel.setAttribute('role', 'button');
    moreVideosLabel.setAttribute('aria-label', 'Custom Button');

    moreVideosButton.appendChild(moreVideosLabel);
  }

  /**
   * Shows the "more videos" toast notification.
   */
  showMoreVideosToast() {
    if (this.moreVideosToastShown) return;

    import('./newvideo-toast').then(toastModule => {
      const toastContainer = this.root_.querySelector<HTMLElement>('.newvideo__toast-container');

      if (toastContainer) {
        new toastModule.NewVideoToast({
          parentElement: toastContainer,
          baseComponentPath: this.componentPath
        });

        PubSub.subscribe('newvideo-toast:clicked', () => {
          if (this.eventPhase === 'running') {
            this.resumePlaybackAfterClosingRevolver = true;
          }

          this.showRevolver('more-videos-notification', false);
        });
      }
    }).catch(e => this.logUnknownError(e));

    this.moreVideosToastShown = true;
  }
}
